import React, { useState } from "react";
import LayersIcon from '@mui/icons-material/Layers';
import ScienceIcon from '@mui/icons-material/Science';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const CondicionesMaduracionSection = ({
  condicionesRecords,
  maduracionRecords,
  isFormOpen,
  setIsFormOpen,
  condMesAnio,
  setCondMesAnio,
  condNoLote,
  setCondNoLote,
  condDailyData,
  setCondDailyData,
  condObservaciones,
  setCondObservaciones,
  handleSaveCondiciones
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Helper to handle daily log input updates
  const handleDailyChange = (dayIndex, field, value) => {
    const updated = [...condDailyData];
    updated[dayIndex] = {
      ...updated[dayIndex],
      [field]: value
    };
    setCondDailyData(updated);
  };

  // If viewing details of a previously submitted month record
  if (selectedRecord) {
    return (
      <div className="animate-fadeIn">
        <div className="history-section-header">
          <button 
            onClick={() => setSelectedRecord(null)} 
            className="btn-secondary-custom" 
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px" }}
          >
            <ArrowBackIcon style={{ fontSize: 16 }} />
            <span>Volver al Historial</span>
          </button>
          <h2>Detalle de Condiciones - {selectedRecord.mesAnio}</h2>
        </div>

        <div className="detail-card detail-card-accent-left-green" style={{ marginBottom: "24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px" }}>
            <div>
              <small style={{ color: "var(--text-muted)", fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Mes / Año</small>
              <p style={{ margin: "4px 0 0 0", fontSize: "15px", fontWeight: 800 }}>{selectedRecord.mesAnio}</p>
            </div>
            <div>
              <small style={{ color: "var(--text-muted)", fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Lote Asociado</small>
              <p style={{ margin: "4px 0 0 0", fontSize: "15px", fontWeight: 800 }}>{selectedRecord.noLote}</p>
            </div>
            <div>
              <small style={{ color: "var(--text-muted)", fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>V.Bo. Inspector</small>
              <p style={{ margin: "4px 0 0 0", fontSize: "14px", fontWeight: 600 }}>{selectedRecord.inspector}</p>
            </div>
            <div>
              <small style={{ color: "var(--text-muted)", fontSize: "11px", textTransform: "uppercase", fontWeight: 700 }}>Estado</small>
              <p style={{ margin: "4px 0 0 0" }}>
                <span className={`badge-status ${selectedRecord.status}`}>
                  {selectedRecord.status === "aprobado" ? "Aprobado" : "En revisión"}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="records-list-wrapper">
          <table className="records-table">
            <thead>
              <tr>
                <th>Día</th>
                <th>Hora</th>
                <th>Temp. Ambiente (°C)</th>
                <th>% Humedad Relativa</th>
                <th>Día</th>
                <th>Hora</th>
                <th>Temp. Ambiente (°C)</th>
                <th>% Humedad Relativa</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 16 }).map((_, i) => {
                const day1 = selectedRecord.registros[i];
                const day2 = selectedRecord.registros[i + 16]; // Days 17 to 31
                return (
                  <tr key={i}>
                    {/* Day 1-16 */}
                    <td style={{ fontWeight: 800, background: "rgba(74,93,35,0.05)" }}>{day1 ? day1.dia : i + 1}</td>
                    <td>{day1?.hora || "-"}</td>
                    <td style={{ color: "var(--copper)" }}>{day1?.temp ? `${day1.temp} °C` : "-"}</td>
                    <td>{day1?.hum ? `${day1.hum} %` : "-"}</td>

                    {/* Day 17-31 */}
                    {day2 ? (
                      <>
                        <td style={{ fontWeight: 800, background: "rgba(74,93,35,0.05)" }}>{day2.dia}</td>
                        <td>{day2.hora || "-"}</td>
                        <td style={{ color: "var(--copper)" }}>{day2.temp ? `${day2.temp} °C` : "-"}</td>
                        <td>{day2.hum ? `${day2.hum} %` : "-"}</td>
                      </>
                    ) : (
                      <>
                        <td style={{ fontWeight: 800, background: "rgba(74,93,35,0.05)" }}>{i + 17 <= 31 ? i + 17 : ""}</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // List view (Main history table of recorded months)
  if (!isFormOpen) {
    return (
      <div className="animate-fadeIn">
        <div className="history-section-header">
          <h2>Registro de Condiciones de Maduración (Sección VIII)</h2>
          <button 
            onClick={() => setIsFormOpen(true)}
            className="btn-add-new"
          >
            <AddIcon style={{ fontSize: 18 }} />
            <span>Registrar Nuevo Mes</span>
          </button>
        </div>

        <div className="records-list-wrapper">
          <table className="records-table">
            <thead>
              <tr>
                <th>Mes / Año</th>
                <th>Lote Asociado</th>
                <th>Días Registrados</th>
                <th>Temp. Promedio (°C)</th>
                <th>Humedad Promedio (%)</th>
                <th>Observaciones</th>
                <th>V.Bo. Inspector</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {condicionesRecords.map((rec, i) => {
                const validDays = rec.registros.filter(d => d.temp || d.hum);
                const avgTemp = validDays.length ? (validDays.reduce((acc, curr) => acc + parseFloat(curr.temp || 0), 0) / validDays.length).toFixed(1) : "-";
                const avgHum = validDays.length ? (validDays.reduce((acc, curr) => acc + parseFloat(curr.hum || 0), 0) / validDays.length).toFixed(1) : "-";

                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 800 }}>{rec.mesAnio}</td>
                    <td>{rec.noLote}</td>
                    <td>{validDays.length} / 31 días</td>
                    <td style={{ color: "var(--copper)", fontWeight: 700 }}>{avgTemp} {avgTemp !== "-" ? "°C" : ""}</td>
                    <td>{avgHum} {avgHum !== "-" ? "%" : ""}</td>
                    <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {rec.observaciones}
                    </td>
                    <td style={{ fontSize: "11px" }}>{rec.inspector}</td>
                    <td>
                      <span className={`badge-status ${rec.status}`}>
                        {rec.status === "aprobado" ? "Aprobado" : rec.status === "revision" ? "En revisión" : "Borrador"}
                      </span>
                    </td>
                    <td>
                      <button 
                        onClick={() => setSelectedRecord(rec)}
                        className="btn-secondary-custom"
                        style={{ padding: "4px 8px", fontSize: "12px" }}
                      >
                        Ver Detalle
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Form View (Create/Edit Month Log Sheet)
  return (
    <div className="animate-fadeIn">
      <div className="history-section-header">
        <button 
          onClick={() => setIsFormOpen(false)} 
          className="btn-secondary-custom" 
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px" }}
        >
          <ArrowBackIcon style={{ fontSize: 16 }} />
          <span>Volver al Historial</span>
        </button>
        <h2>Nuevo Registro Mensual de Condiciones - Sección VIII</h2>
      </div>

      <div className="cards-container-grid">
        {/* Month identification details */}
        <section className="detail-card detail-card-accent-left-green" style={{ gridColumn: "1 / -1" }}>
          <header className="detail-card-header">
            <div className="detail-card-icon-wrapper-green">
              <CalendarMonthIcon />
            </div>
            <div className="detail-card-title-wrapper">
              <h2>Identificación del Periodo</h2>
              <p>Mes, año y lote de mezcal en barrica/garrafa a monitorear</p>
            </div>
          </header>

          <div className="species-grid">
            <div className="custom-input-group">
              <input 
                type="text" 
                value={condMesAnio} 
                onChange={e => setCondMesAnio(e.target.value)} 
                className="custom-input-field"
                placeholder="Ej. Julio 2026"
              />
              <label className="custom-input-label">Mes / Año</label>
            </div>

            <div className="custom-input-group">
              <select 
                value={condNoLote} 
                onChange={e => setCondNoLote(e.target.value)}
                className="custom-select-field"
                style={{ padding: "18px 16px 8px 16px" }}
              >
                <option value="">-- Seleccionar Lote --</option>
                {maduracionRecords.map((r, idx) => (
                  <option key={idx} value={r.noLote}>
                    {r.noLote} (Recipientes: {r.invFinalRecipientes})
                  </option>
                ))}
              </select>
              <label className="custom-input-label">Nº de Lote en Maduración</label>
            </div>
          </div>
        </section>

        {/* Daily grid sheet (split into two columns 1-15 & 16-31 for visual space) */}
        <section className="detail-card detail-card-accent-left-copper" style={{ gridColumn: "1 / -1" }}>
          <header className="detail-card-header">
            <div className="detail-card-icon-wrapper-copper">
              <DeviceThermostatIcon />
            </div>
            <div className="detail-card-title-wrapper">
              <h2>Monitoreo Diario de Temperatura y Humedad</h2>
              <p>Ingrese las mediciones registradas para cada día del mes</p>
            </div>
          </header>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginTop: "16px" }}>
            
            {/* Days 1 to 15 */}
            <div>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--agave-green)", borderBottom: "1px solid var(--glass-border)", paddingBottom: "8px", marginBottom: "16px" }}>
                Días 1 al 15
              </h3>
              {Array.from({ length: 15 }).map((_, i) => {
                const dayNum = i + 1;
                return (
                  <div key={dayNum} style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "12px" }}>
                    <span style={{ fontWeight: 800, width: "32px", fontSize: "13px" }}>Día {dayNum}</span>
                    <input 
                      type="text" 
                      placeholder="Hora" 
                      value={condDailyData[i]?.hora || ""}
                      onChange={(e) => handleDailyChange(i, "hora", e.target.value)}
                      className="custom-input-field" 
                      style={{ padding: "8px 12px", height: "38px" }}
                    />
                    <input 
                      type="number" 
                      placeholder="Temp °C" 
                      value={condDailyData[i]?.temp || ""}
                      onChange={(e) => handleDailyChange(i, "temp", e.target.value)}
                      className="custom-input-field" 
                      style={{ padding: "8px 12px", height: "38px" }}
                    />
                    <input 
                      type="number" 
                      placeholder="Hum %" 
                      value={condDailyData[i]?.hum || ""}
                      onChange={(e) => handleDailyChange(i, "hum", e.target.value)}
                      className="custom-input-field" 
                      style={{ padding: "8px 12px", height: "38px" }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Days 16 to 31 */}
            <div>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--agave-green)", borderBottom: "1px solid var(--glass-border)", paddingBottom: "8px", marginBottom: "16px" }}>
                Días 16 al 31
              </h3>
              {Array.from({ length: 16 }).map((_, i) => {
                const dayNum = i + 16;
                const idx = i + 15;
                return (
                  <div key={dayNum} style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "12px" }}>
                    <span style={{ fontWeight: 800, width: "32px", fontSize: "13px" }}>Día {dayNum}</span>
                    <input 
                      type="text" 
                      placeholder="Hora" 
                      value={condDailyData[idx]?.hora || ""}
                      onChange={(e) => handleDailyChange(idx, "hora", e.target.value)}
                      className="custom-input-field" 
                      style={{ padding: "8px 12px", height: "38px" }}
                    />
                    <input 
                      type="number" 
                      placeholder="Temp °C" 
                      value={condDailyData[idx]?.temp || ""}
                      onChange={(e) => handleDailyChange(idx, "temp", e.target.value)}
                      className="custom-input-field" 
                      style={{ padding: "8px 12px", height: "38px" }}
                    />
                    <input 
                      type="number" 
                      placeholder="Hum %" 
                      value={condDailyData[idx]?.hum || ""}
                      onChange={(e) => handleDailyChange(idx, "hum", e.target.value)}
                      className="custom-input-field" 
                      style={{ padding: "8px 12px", height: "38px" }}
                    />
                  </div>
                );
              })}
            </div>

          </div>

          <div className="custom-input-group" style={{ marginTop: 24 }}>
            <textarea 
              value={condObservaciones} 
              onChange={e => setCondObservaciones(e.target.value)} 
              className="custom-input-field"
              style={{ height: "80px", resize: "none", padding: "14px" }}
              placeholder="Notas generales..."
            />
            <label className="custom-input-label">Observaciones</label>
          </div>
        </section>
      </div>

      <div className="button-row">
        <button type="button" className="btn-secondary-custom" onClick={() => setIsFormOpen(false)}>Volver</button>
        <button 
          type="button" 
          className="btn-primary-custom" 
          onClick={() => handleSaveCondiciones("revision")}
        >
          <CheckCircleIcon style={{ fontSize: 16 }} />
          <span>Registrar Monitoreo Sección VIII</span>
        </button>
      </div>
    </div>
  );
};

export default CondicionesMaduracionSection;
